import api from "./axios";

export const startRecording = (matchId, tier = "B2C") =>
  api.post("/api/scoring/recordings/start/", { match_id: matchId, tier }).then(r => r.data);

// Returns the in-flight MatchRecording for a match + the S3 parts
// already uploaded for it. Used by the recorder on mount to resume
// after a refresh without re-clicking Start. 404 if none active.
export const getActiveRecording = (matchId) =>
  api.get(`/api/matches/matches/${matchId}/active-recording/`).then(r => r.data);

export const presignPart = (recordingId, partNumber) =>
  api.post(`/api/scoring/recordings/${recordingId}/part/`, { part_number: partNumber }).then(r => r.data);

// Standalone-chunk presign — sibling to presignPart. Returns a PUT URL
// pointing at live-chunks/<recording_id>/part-<N>.<ext>. The recorder
// dual-PUTs each chunk to both the multipart part URL AND this URL so
// per-ball clips can be sliced mid-match (multipart parts aren't
// readable as objects until CompleteMultipart fires).
export const presignLiveChunk = (recordingId, partNumber, ext = "mp4") =>
  api.post(`/api/scoring/recordings/${recordingId}/live-chunk-presign/`, {
    part_number: partNumber, ext,
  }).then(r => r.data);

// Tell the backend the standalone chunk is now in S3 — it slices a
// per-ball MP4 for every Ball in the match whose video_start_ms falls
// inside this chunk's range. Fire-and-forget from the recorder's POV.
export const processLiveChunk = (recordingId, partNumber) =>
  api.post(`/api/scoring/recordings/${recordingId}/process-live-chunk/${partNumber}/`).then(r => r.data);

export const completeRecording = (recordingId, payload) =>
  api.post(`/api/scoring/recordings/${recordingId}/complete/`, payload).then(r => r.data);

// v2 finalize — kicks off EC2 master concat. SPA then polls
// /recordings/<id>/state/ for the Stop-time overlay progress bar.
export const finalizeRecording = (recordingId, { duration_ms } = {}) =>
  api.post(`/api/scoring/recordings/${recordingId}/finalize/`, { duration_ms }).then(r => r.data);

// Lightweight state poll — finalize_phase, progress_pct, error.
export const getRecordingState = (recordingId) =>
  api.get(`/api/scoring/recordings/${recordingId}/state/`).then(r => r.data);

export const abortRecording = (recordingId) =>
  api.post(`/api/scoring/recordings/${recordingId}/abort/`).then(r => r.data);

// analyst side
export const getPlaybackUrl = (recordingId) =>
  api.get(`/api/matches/recordings/${recordingId}/playback/`).then(r => r.data);

// Per-ball clip — presigned S3 URL + clip start/end ms.
export const getBallVideoPlayback = (ballId) =>
  api.get(`/api/scoring/balls/${ballId}/video/playback/`).then(r => r.data);

// Post-hoc upload of a per-ball clip from QC — for balls that missed the
// live recording (camera glitch, late start, etc.). The backend writes
// straight to S3 + upserts the BallVideo row; on success the playback
// endpoint immediately serves the file.
export const uploadBallVideo = (ballId, file, { onProgress } = {}) => {
  const fd = new FormData();
  fd.append("video", file);
  return api.post(`/api/scoring/balls/${ballId}/video/`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round(100 * e.loaded / e.total));
    },
  }).then(r => r.data);
};

// Master MP4 (the full match recording) — drives the EditBallDialog
// Video Clip tab so the operator can scrub the whole match and pick
// new clip-in / clip-out points for any ball.
export const getMatchMasterRecording = (matchId) =>
  api.get(`/api/matches/matches/${matchId}/recording-master/`).then(r => r.data);

// Save new clip-in / clip-out offsets for a ball AND re-invoke the
// slicer so the per-ball clip MP4 in S3 is replaced. Returns 202 with
// {status: "queued", output_key, ...}.
export const resliceBall = (ballId, { video_start_ms, video_end_ms }) =>
  api.post(`/api/scoring/balls/${ballId}/reslice/`, {
    video_start_ms, video_end_ms,
  }).then(r => r.data);

// Tier 2 match-health diagnostic — recording state + S3 chunk count +
// per-ball clip presence + ball/commentary roll-up. Used by the
// /admin/match-health/<id> page and is also dumpable as a downloadable
// JSON bundle for support tickets.
export const getMatchHealth = (matchId) =>
  api.get(`/api/matches/matches/${matchId}/health/`).then(r => r.data);

export const downloadMatchHealthBundleUrl = (matchId) =>
  `/api/matches/matches/${matchId}/health/bundle/`;
